/**
 * Throttled "user is active" ping used to keep the scale-to-zero Cloud Run
 * services (backend, engine, ClamAV) warm while someone is really working.
 *
 * IMPORTANT: notify() must only be called from real DOM user input (see
 * installUserActivityListeners in sessionActivity.ts). Background polling,
 * WebSocket traffic, API responses and token refresh must NOT call it —
 * otherwise an idle open pane would keep the whole environment running.
 *
 * Pure module (no config / network imports) so it can be unit-tested in node.
 *
 * Mirror of dashboard/src/utils/activityPing.ts (kept in sync by hand; there is
 * no shared frontend package).
 */

/** At most one ping per this long. Cloud Run keeps an idle instance ~15 min. */
export const PING_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes

export interface ActivityPingerOptions {
  /** Fire-and-forget sender. Must never throw into the caller. */
  send: () => void;
  intervalMs?: number;
  now?: () => number;
  /** True when the tab is hidden; a hidden tab never pings. */
  isHidden?: () => boolean;
}

export interface ActivityPinger {
  /** Report a real user interaction. Returns true when a ping was sent. */
  notify: () => boolean;
  /** Forget the last ping (e.g. on logout) so the next interaction pings. */
  reset: () => void;
}

export function createActivityPinger(options: ActivityPingerOptions): ActivityPinger {
  const intervalMs = options.intervalMs ?? PING_INTERVAL_MS;
  const now = options.now ?? (() => Date.now());
  const isHidden =
    options.isHidden ??
    (() => typeof document !== 'undefined' && document.visibilityState === 'hidden');

  let lastSentAt: number | null = null;

  return {
    notify(): boolean {
      if (isHidden()) return false;
      const ts = now();
      if (lastSentAt != null && ts - lastSentAt < intervalMs) return false;
      lastSentAt = ts;
      try {
        options.send();
      } catch {
        /* the ping is best-effort and must never affect the UI */
      }
      return true;
    },
    reset(): void {
      lastSentAt = null;
    },
  };
}
